import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCheckLight,
  PiFileTextLight,
  PiMagnifyingGlassLight,
  PiSlidersHorizontalLight,
  PiInfoLight,
} from 'react-icons/pi';

import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { ClientSelectorClient } from '../shared/ClientSelector';
import ClientSelector from '../shared/ClientSelector';
import { Collaborator } from '../shared/CollaboratorSelector';
import FileOrDescriptionSelector from '../shared/FileOrDescriptionSelector';
import IndexSourceSelector from './IndexSourceSelector';
import { hasCasesSupport } from './matterConstants';
import Toggle from '../shared/TailwindComponents/Toggle';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import { useAppDispatch } from '../../store';
import { showModal, hideModal } from '../../store/actions/modalActions';
import {
  PiScalesLight,
  PiGavelLight,
  PiDatabaseLight,
  PiListChecksLight,
  PiChatTextLight,
  PiTextAlignLeftLight,
  PiNoteLight,
  PiSparkleLight,
} from 'react-icons/pi';
import { WizardStepTip, FeatureTour, matterWizardStepTips, matterWizardTourSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

// ─── Types ───────────────────────────────────────────────────────────

type WizardStep = 'basics' | 'documents' | 'research' | 'review';

interface AnalysisOptionsValue {
  fetchLaws: boolean;
  fetchPrecedents: boolean;
  fetchDataSource: boolean;
  createTasks: boolean;
  createQuestionnaire: boolean;
  generateDescription: boolean;
  generateNotes: boolean;
  generateDraftSuggestions: boolean;
  generateInsightSuggestions: boolean;
}

const DEFAULT_ANALYSIS_OPTIONS: AnalysisOptionsValue = {
  fetchLaws: true,
  fetchPrecedents: true,
  fetchDataSource: true,
  createTasks: true,
  createQuestionnaire: true,
  generateDescription: true,
  generateNotes: true,
  generateDraftSuggestions: true,
  generateInsightSuggestions: true,
};

export interface MatterWizardData {
  matterTitle: string;
  category: string;
  clients: ClientSelectorClient[];
  collaborators: Collaborator[];
  filesOrDescription: { files: File[]; description: string };
  language: string;
  jurisdiction: string;
  selectedIndexes: string[];
  dataSources: string[];
  analysisOptions: AnalysisOptionsValue;
  projectUUID?: string;
}

interface MatterWizardProps {
  initialTitle?: string;
  categories: DropdownOption[];
  languages: DropdownOption[];
  jurisdictions: DropdownOption[];
  clientOptions: DropdownOption[];
  clientRoleOptions: DropdownOption[];
  dataSourceOptions: DropdownOption[];
  projectOptions?: DropdownOption[];
  defaultLanguage: string;
  defaultJurisdiction: string;
  autoAddClient: { ClientID: string; ClientRoles: string[] } | null;
  onAddNewClient: () => void;
  onSubmit: (data: MatterWizardData) => void;
  onCancel: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
}

// ─── Step definitions ────────────────────────────────────────────────

const STEPS: { key: WizardStep; label: string; icon: React.ReactNode }[] = [
  { key: 'basics', label: 'Details', icon: <PiInfoLight className="w-4 h-4" /> },
  { key: 'documents', label: 'Documents', icon: <PiFileTextLight className="w-4 h-4" /> },
  { key: 'research', label: 'Sources', icon: <PiMagnifyingGlassLight className="w-4 h-4" /> },
  { key: 'review', label: 'Review & Launch', icon: <PiSlidersHorizontalLight className="w-4 h-4" /> },
];

// ─── Review panel: custom component for BaseForm ─────────────────────

interface AnalysisOptionsPanelProps {
  value: AnalysisOptionsValue;
  onChange: (val: AnalysisOptionsValue) => void;
  jurisdiction: string;
  hasDataSources: boolean;
  summary: {
    title: string;
    docCount: number;
    jurisdiction: string;
    jurisdictionLabel: string;
    language: string;
    languageLabel: string;
    clientCount: number;
    collaboratorCount: number;
    indexCount: number;
    dataSourceCount: number;
    category: string;
  };
}

const AnalysisOptionsPanel: React.FC<AnalysisOptionsPanelProps> = ({
  value,
  onChange,
  jurisdiction,
  hasDataSources,
  summary,
}) => {
  const { t } = useTranslation();
  const showCases = hasCasesSupport(jurisdiction);

  const toggleOption = (key: keyof AnalysisOptionsValue) => {
    onChange({ ...value, [key]: !value[key] });
  };

  type ToggleOpt = { key: keyof AnalysisOptionsValue; label: string; description: string; icon: React.ReactNode };

  const researchOpts: ToggleOpt[] = [
    { key: 'fetchLaws', label: t('Applicable Laws'), description: t('Search for statutes, regulations, and legal provisions relevant to your matter.'), icon: <PiScalesLight className="w-5 h-5" /> },
    ...(showCases ? [{ key: 'fetchPrecedents' as const, label: t('Precedents / Case Law'), description: t('Find court rulings and judicial decisions that may apply to your matter.'), icon: <PiGavelLight className="w-5 h-5" /> }] : []),
    ...(hasDataSources ? [{ key: 'fetchDataSource' as const, label: t('Connected Data Sources'), description: t('Search your own uploaded document collections for relevant information.'), icon: <PiDatabaseLight className="w-5 h-5" /> }] : []),
  ];

  const genOpts: ToggleOpt[] = [
    { key: 'createTasks', label: t('Suggested Tasks'), description: t('Generate a prioritized action plan with recommended next steps.'), icon: <PiListChecksLight className="w-5 h-5" /> },
    { key: 'createQuestionnaire', label: t('Client Questionnaire'), description: t('Create a tailored set of questions to gather key information from your client.'), icon: <PiChatTextLight className="w-5 h-5" /> },
    { key: 'generateDescription', label: t('Matter Description'), description: t('Produce a concise summary describing the scope and context of the matter.'), icon: <PiTextAlignLeftLight className="w-5 h-5" /> },
    { key: 'generateNotes', label: t('Case Notes'), description: t('Extract key facts, dates, and observations into structured case notes.'), icon: <PiNoteLight className="w-5 h-5" /> },
    { key: 'generateDraftSuggestions', label: t('Draft Suggestions'), description: t('Suggest document drafts such as letters, contracts, or legal briefs.'), icon: <PiFileTextLight className="w-5 h-5" /> },
    { key: 'generateInsightSuggestions', label: t('Insight Suggestions'), description: t('Surface strategic insights and potential risks based on the analysis.'), icon: <PiSparkleLight className="w-5 h-5" /> },
  ];

  const renderToggleCard = (opt: ToggleOpt) => (
    <div
      key={opt.key}
      className={`flex items-start justify-between gap-3 rounded-lg border p-3 transition-all duration-200 bg-white dark:bg-gray-800 ${
        value[opt.key]
          ? 'border-primary-300 bg-primary-50/50 dark:border-primary-700 dark:bg-primary-900/20'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className={`flex-shrink-0 mt-0.5 transition-colors duration-200 ${
          value[opt.key]
            ? 'text-primary-600 dark:text-primary-400'
            : 'text-gray-400 dark:text-gray-500'
        }`}>
          {opt.icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{opt.label}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{opt.description}</p>
        </div>
      </div>
      <div className="flex-shrink-0 mt-0.5">
        <Toggle enabled={value[opt.key]} onToggle={() => toggleOption(opt.key)} />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div data-tour-id="review-summary" className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">{t('Summary')}</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <span className="text-gray-500 dark:text-gray-400">{t('Title')}</span>
          <span className="text-gray-900 dark:text-gray-100 font-medium truncate">{summary.title}</span>
          {summary.category && (
            <>
              <span className="text-gray-500 dark:text-gray-400">{t('Category')}</span>
              <span className="text-gray-900 dark:text-gray-100">{summary.category}</span>
            </>
          )}
          <span className="text-gray-500 dark:text-gray-400">{t('Documents')}</span>
          <span className="text-gray-900 dark:text-gray-100">
            {summary.docCount > 0
              ? (summary.docCount === 1 ? t('1 file') : t('{{count}} files', { count: summary.docCount }))
              : t('Text description')}
          </span>
          <span className="text-gray-500 dark:text-gray-400">{t('Jurisdiction')}</span>
          <span className="text-gray-900 dark:text-gray-100">{summary.jurisdictionLabel}</span>
          <span className="text-gray-500 dark:text-gray-400">{t('Language')}</span>
          <span className="text-gray-900 dark:text-gray-100">{summary.languageLabel}</span>
          <span className="text-gray-500 dark:text-gray-400">{t('Legal Sources')}</span>
          <span className="text-gray-900 dark:text-gray-100">
            {summary.indexCount > 0
              ? (summary.indexCount === 1 ? t('1 source') : t('{{count}} sources', { count: summary.indexCount }))
              : t('None selected')}
          </span>
          {summary.dataSourceCount > 0 && (
            <>
              <span className="text-gray-500 dark:text-gray-400">{t('Data Sources')}</span>
              <span className="text-gray-900 dark:text-gray-100">
                {summary.dataSourceCount === 1 ? t('1 data source') : t('{{count}} data sources', { count: summary.dataSourceCount })}
              </span>
            </>
          )}
          {summary.clientCount > 0 && (
            <>
              <span className="text-gray-500 dark:text-gray-400">{t('Clients')}</span>
              <span className="text-gray-900 dark:text-gray-100">
                {summary.clientCount === 1 ? t('1 client') : t('{{count}} clients', { count: summary.clientCount })}
              </span>
            </>
          )}
          {summary.collaboratorCount > 0 && (
            <>
              <span className="text-gray-500 dark:text-gray-400">{t('Access')}</span>
              <span className="text-gray-900 dark:text-gray-100">
                {summary.collaboratorCount === 1 ? t('1 person') : t('{{count}} people', { count: summary.collaboratorCount })}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Research toggles */}
      <div data-tour-id="review-research">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
          {t('Legal Research')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {researchOpts.map(renderToggleCard)}
        </div>
      </div>

      {/* Generation toggles */}
      <div data-tour-id="review-generation">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
          {t('PONS Generation')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {genOpts.map(renderToggleCard)}
        </div>
      </div>
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────

const MatterWizard: React.FC<MatterWizardProps> = ({
  initialTitle = '',
  categories,
  languages,
  jurisdictions,
  clientOptions,
  clientRoleOptions,
  dataSourceOptions,
  projectOptions = [],
  defaultLanguage,
  defaultJurisdiction,
  autoAddClient,
  onAddNewClient,
  onSubmit,
  onCancel,
  onBack,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Wizard navigation (declared before tour so we can pass currentStep)
  const [currentStep, setCurrentStep] = useState<WizardStep>('basics');
  const currentIdx = STEPS.findIndex((s) => s.key === currentStep);

  // ── Per-step guided tour ──
  const currentTourSteps = matterWizardTourSteps[currentStep] || [];
  const {
    isRunning: isWizardTourRunning,
    handleJoyrideCallback: handleWizardTourCallback,
  } = useFeatureTour({
    tourId: `matter-wizard-tour-${currentStep}`,
    steps: currentTourSteps,
    readyToShow: currentTourSteps.length > 0,
    autoStartDelay: 600,
  });

  // Track whether any field has been touched across all steps
  const wizardDirtyRef = useRef(false);

  // Accumulated form data across steps
  const [accumulated, setAccumulated] = useState<Partial<MatterWizardData>>({
    matterTitle: initialTitle,
    category: categories[0]?.value || '',
    clients: [],
    collaborators: [],
    filesOrDescription: { files: [], description: '' },
    language: defaultLanguage || languages[0]?.value || '',
    jurisdiction: defaultJurisdiction || jurisdictions[0]?.value || '',
    selectedIndexes: [],
    dataSources: [],
    analysisOptions: DEFAULT_ANALYSIS_OPTIONS,
    projectUUID: '',
  });

  const goPrev = useCallback(() => {
    if (currentIdx > 0) {
      setCurrentStep(STEPS[currentIdx - 1].key);
    } else {
      onBack();
    }
  }, [currentIdx, onBack]);

  // Capture form data and advance to next step
  const handleStepSubmit = useCallback((formData: Record<string, any>) => {
    setAccumulated((prev) => ({ ...prev, ...formData }));
    if (currentIdx < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIdx + 1].key);
    }
  }, [currentIdx]);

  // Final submit on review step
  const handleFinalSubmit = useCallback((formData: Record<string, any>) => {
    const merged = { ...accumulated, ...formData };
    onSubmit({
      matterTitle: (merged.matterTitle || '').trim(),
      category: merged.category || '',
      clients: merged.clients || [],
      collaborators: merged.collaborators || [],
      filesOrDescription: merged.filesOrDescription || { files: [], description: '' },
      language: merged.language || '',
      jurisdiction: merged.jurisdiction || '',
      selectedIndexes: merged.selectedIndexes || [],
      dataSources: merged.dataSources || [],
      analysisOptions: merged.analysisOptions || DEFAULT_ANALYSIS_OPTIONS,
    });
  }, [accumulated, onSubmit]);

  // Sync accumulated data when BaseForm values change (keeps accumulated in sync for back navigation)
  const handleValuesChange = useCallback((values: Record<string, any>) => {
    wizardDirtyRef.current = true;
    setAccumulated((prev) => ({ ...prev, ...values }));
  }, []);

  // Wrap onCancel with a confirmation modal if the user has entered any data
  const handleCancel = useCallback(() => {
    if (wizardDirtyRef.current || currentIdx > 0) {
      dispatch(
        showModal({
          showModal: true,
          type: 'confirmation',
          message: t('Discard Changes'),
          subMsg: t('You have unsaved changes. Are you sure you want to discard them?'),
          onConfirm: () => {
            dispatch(hideModal());
            onCancel();
          },
          onCancel: () => {
            dispatch(hideModal());
          },
        } as any)
      );
    } else {
      onCancel();
    }
  }, [currentIdx, dispatch, onCancel, t]);

  // ─── Step indicator ──────────────────────────────────────────────

  const renderStepIndicator = () => (
    <nav className="px-6 pt-4 pb-2 flex-shrink-0" data-tour-id="wizard-step-indicator">
      <ol className="flex items-center gap-2 max-w-3xl mx-auto">
        {STEPS.map((step, idx) => {
          const isCompleted = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          return (
            <li key={step.key} className="flex items-center gap-2 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                    isCompleted
                      ? 'border-primary-500 bg-primary-500 text-white'
                      : isCurrent
                      ? 'border-primary-500 bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'border-gray-300 bg-white text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500'
                  }`}
                >
                  {isCompleted ? <PiCheckLight className="w-4 h-4" /> : step.icon}
                </div>
                <span
                  className={`text-xs font-medium truncate hidden sm:block ${
                    isCurrent
                      ? 'text-primary-600 dark:text-primary-400'
                      : isCompleted
                      ? 'text-gray-700 dark:text-gray-300'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {t(step.label)}
                </span>
              </div>
              {idx < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-px transition-colors duration-200 ${
                    idx < currentIdx ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );

  // ─── Step configs for BaseForm ─────────────────────────────────────

  const stepTitles: Record<WizardStep, { title: string; description: string }> = {
    basics: {
      title: t('Matter Details'),
      description: t('Name your matter and set where it applies. Clients and collaborators can also be added later.'),
    },
    documents: {
      title: t('Documents'),
      description: t('Upload the documents PONS will analyze - contracts, pleadings, correspondence - or describe the matter in your own words.'),
    },
    research: {
      title: t('Legal Sources'),
      description: t('Choose which legal databases and knowledge bases PONS should search during analysis.'),
    },
    review: {
      title: t('Review & Launch'),
      description: t('Confirm your setup and choose which PONS features to run. Everything is enabled by default - toggle off anything you don\u2019t need.'),
    },
  };

  // Step 1: Basics - now includes language & jurisdiction
  const basicsSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      fields: [
        {
          name: 'matterTitle',
          type: 'input',
          label: t('Matter Title'),
          placeholder: t('e.g. Smith v. Johnson, IP License Review, Tenant Dispute...'),
          required: true,
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    {
      gridCols: categories.length > 0 ? 3 : 2,
      fields: [
        ...(categories.length > 0 ? [{
          name: 'category',
          type: 'dropdown',
          label: t('Category'),
          options: categories,
          placeholder: t('Select category'),
          customProps: { fullWidth: true },
          gridSize: 'third',
        } as BaseFormFieldConfig] : []),
        {
          name: 'language',
          type: 'dropdown',
          label: t('Language'),
          options: languages,
          placeholder: t('Select language'),
          required: true,
          customProps: { fullWidth: true },
          gridSize: categories.length > 0 ? 'third' : 'half',
        } as BaseFormFieldConfig,
        {
          name: 'jurisdiction',
          type: 'dropdown',
          label: t('Jurisdiction'),
          options: jurisdictions,
          placeholder: t('Select jurisdiction'),
          required: true,
          customProps: { fullWidth: true },
          gridSize: categories.length > 0 ? 'third' : 'half',
        } as BaseFormFieldConfig,
      ],
    },
    {
      title: t('Clients'),
      description: t('Select one or more clients for this matter. You can also add clients later.'),
      fields: [
        {
          name: 'clients',
          type: 'custom',
          label: t('Clients'),
          customComponent: ClientSelector,
          customProps: {
            clientOptions,
            roleOptions: clientRoleOptions,
            onAddNewClient,
            autoAddClient,
          },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    {
      title: t('Access'),
      description: t('Add people who can access this matter'),
      fields: [
        {
          name: 'collaborators',
          type: 'collaboratorSelector',
          label: t('Access'),
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    ...(projectOptions.length > 0 ? [{
      fields: [
        {
          name: 'projectUUID',
          type: 'dropdown' as const,
          label: t('Link to Project'),
          placeholder: t('Select a project (optional)'),
          required: false,
          gridSize: 'full' as const,
          customProps: { fullWidth: true },
          options: projectOptions,
        } as BaseFormFieldConfig,
      ],
    }] : []),
  ], [categories, languages, jurisdictions, clientOptions, clientRoleOptions, onAddNewClient, autoAddClient, projectOptions, t]);

  const basicsSchema = useMemo(() => {
    const s = new FormSchema('WizardBasicsSchema');
    s.addField(new FormField('matterTitle', 'text', true, t('Matter Title'), {
      customValidator: (value: any) => {
        if (!value || !String(value).trim()) {
          return { isValid: false, error: t('Matter title is required') };
        }
        return { isValid: true, sanitized: String(value).trim() };
      },
    }));
    s.addField(new FormField('category', 'text', false, t('Category')));
    s.addField(new FormField('language', 'text', true, t('Language')));
    s.addField(new FormField('jurisdiction', 'text', true, t('Jurisdiction')));
    s.addField(new FormField('clients', 'text', false, t('Clients')));
    s.addField(new FormField('collaborators', 'text', false, t('Access')));
    return s;
  }, [t]);

  // Step 2: Documents
  const documentsSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      fields: [
        {
          name: 'filesOrDescription',
          type: 'custom',
          label: t('Files & Description'),
          customComponent: FileOrDescriptionSelector,
          required: true,
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
  ], [t]);

  const documentsSchema = useMemo(() => {
    const s = new FormSchema('WizardDocumentsSchema');
    s.addField(new FormField('filesOrDescription', 'text', true, t('Files & Description'), {
      customValidator: (value: any) => {
        const v = value || {};
        const hasFiles = v.files && v.files.length > 0;
        const hasDesc = v.description && v.description.trim().length > 0;
        if (!hasFiles && !hasDesc) {
          return { isValid: false, error: t('Upload at least one document or provide a description') };
        }
        return { isValid: true };
      },
    }));
    return s;
  }, [t]);

  // Step 3: Sources - legal indexes + data sources (language/jurisdiction moved to step 1)
  const researchSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      title: t('Legal Databases'),
      description: t('Select which official legal databases PONS should search. These include legislation, case law, preparatory works, and more - depending on your jurisdiction.'),
      fields: [
        {
          name: 'selectedIndexes',
          type: 'custom',
          label: t('Search Sources'),
          customComponent: IndexSourceSelector,
          customProps: {
            jurisdiction: accumulated.jurisdiction || defaultJurisdiction,
          },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    ...(dataSourceOptions.length > 0 ? [{
      title: t('Your Data Sources'),
      description: t('Data sources are your own reusable knowledge bases that you upload to PONS and can use across multiple matters - for example, internal policies, property regulations, industry standards, or previous case research.'),
      fields: [
        {
          name: 'dataSources',
          type: 'dropdown',
          label: t('Data Sources'),
          options: dataSourceOptions,
          placeholder: t('Select data sources (optional)'),
          customProps: { multiple: true, fullWidth: true },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    }] : []),
  ], [dataSourceOptions, accumulated.jurisdiction, defaultJurisdiction, t]);

  const researchSchema = useMemo(() => {
    const s = new FormSchema('WizardResearchSchema');
    s.addField(new FormField('selectedIndexes', 'text', false, t('Search Sources')));
    s.addField(new FormField('dataSources', 'text', false, t('Data Sources')));
    return s;
  }, [t]);

  // Step 4: Review
  const reviewSummary = useMemo(() => ({
    title: accumulated.matterTitle || '',
    docCount: accumulated.filesOrDescription?.files?.length || 0,
    jurisdiction: accumulated.jurisdiction || '',
    jurisdictionLabel: jurisdictions.find((j) => j.value === accumulated.jurisdiction)?.label || accumulated.jurisdiction || '',
    language: accumulated.language || '',
    languageLabel: languages.find((l) => l.value === accumulated.language)?.label || accumulated.language || '',
    clientCount: accumulated.clients?.length || 0,
    collaboratorCount: accumulated.collaborators?.length || 0,
    indexCount: accumulated.selectedIndexes?.length || 0,
    dataSourceCount: accumulated.dataSources?.length || 0,
    category: categories.find((c) => c.value === accumulated.category)?.label || '',
  }), [accumulated, jurisdictions, languages, categories]);

  const reviewSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      fields: [
        {
          name: 'analysisOptions',
          type: 'custom',
          label: t('Analysis Options'),
          customComponent: AnalysisOptionsPanel,
          customProps: {
            jurisdiction: accumulated.jurisdiction || defaultJurisdiction,
            hasDataSources: dataSourceOptions.length > 0,
            summary: reviewSummary,
          },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
  ], [accumulated.jurisdiction, defaultJurisdiction, dataSourceOptions, reviewSummary, t]);

  const reviewSchema = useMemo(() => {
    const s = new FormSchema('WizardReviewSchema');
    s.addField(new FormField('analysisOptions', 'text', false, t('Analysis Options')));
    return s;
  }, [t]);

  // ─── Per-step config ───────────────────────────────────────────────

  const isLastStep = currentIdx === STEPS.length - 1;

  const stepConfigs: Record<WizardStep, {
    sections: BaseFormSectionConfig[];
    schema: FormSchema;
    initialValues: Record<string, any>;
  }> = {
    basics: {
      sections: basicsSections,
      schema: basicsSchema,
      initialValues: {
        matterTitle: accumulated.matterTitle || '',
        category: accumulated.category || categories[0]?.value || '',
        language: accumulated.language || defaultLanguage || languages[0]?.value || '',
        jurisdiction: accumulated.jurisdiction || defaultJurisdiction || jurisdictions[0]?.value || '',
        clients: accumulated.clients || [],
        collaborators: accumulated.collaborators || [],
        projectUUID: accumulated.projectUUID || '',
      },
    },
    documents: {
      sections: documentsSections,
      schema: documentsSchema,
      initialValues: {
        filesOrDescription: accumulated.filesOrDescription || { files: [], description: '' },
      },
    },
    research: {
      sections: researchSections,
      schema: researchSchema,
      initialValues: {
        selectedIndexes: accumulated.selectedIndexes || [],
        dataSources: accumulated.dataSources || [],
      },
    },
    review: {
      sections: reviewSections,
      schema: reviewSchema,
      initialValues: {
        analysisOptions: accumulated.analysisOptions || DEFAULT_ANALYSIS_OPTIONS,
      },
    },
  };

  const config = stepConfigs[currentStep];

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {renderStepIndicator()}
      {matterWizardStepTips[currentStep] && (
        <div className="px-6 pt-4 flex-shrink-0">
          <WizardStepTip
            key={currentStep}
            tipId={`matter-wizard-${currentStep}`}
            title={matterWizardStepTips[currentStep].title}
            content={matterWizardStepTips[currentStep].content}
          />
        </div>
      )}
      <BaseForm
        key={currentStep}
        className="flex-1 min-h-0"
        title={stepTitles[currentStep].title}
        description={stepTitles[currentStep].description}
        sections={config.sections}
        schema={config.schema}
        initialValues={config.initialValues}
        onSubmit={isLastStep ? handleFinalSubmit : handleStepSubmit}
        onCancel={handleCancel}
        onBack={goPrev}
        submitLabel={isLastStep ? t('Create & Analyze') : t('Next')}
        cancelLabel={t('Cancel')}
        backLabel={currentIdx === 0 ? t('Back') : t('Previous')}
        maxWidth="max-w-3xl"
        isSubmitting={isLastStep ? isSubmitting : false}
        showConfirmationModal={false}
        onValuesChange={handleValuesChange}
      />
      <FeatureTour
        steps={currentTourSteps}
        run={isWizardTourRunning}
        onCallback={handleWizardTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default MatterWizard;
